import React, { CSSProperties, FC, MouseEventHandler, RefObject, useCallback, useMemo } from 'react';
import { debounce } from 'underscore';
import RbButton from 'react-bootstrap/Button';
import Spinner from './Spinner';

export interface IProps {
  type?: 'button' | 'reset' | 'submit' | string;
  btnType?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'
    | 'light'
    | 'dark'
    | 'link'
    | 'icon'
    | 'purple';
  outlined?: boolean;
  size?: 'sm' | 'lg';
  className?: string;
  isTableAction?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  title?: string;
  loading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  preventDebounce?: boolean;
  ref?: RefObject<HTMLButtonElement>;
  ariaLabel?: string;
  testId?: string;
}

const btnTypeMap: Partial<
  Record<Exclude<IProps['btnType'], undefined>, (style?: CSSProperties, disabled?: boolean) => CSSProperties>
> = {
  light: (style?: CSSProperties, disabled?: boolean) => {
    return { ...style, borderColor: '#ced4da', color: 'inherit', ...(disabled ? { pointerEvents: 'none' } : {}) };
  },

  purple: (style?: CSSProperties, disabled?: boolean) => {
    return {
      ...style,
      ...{
        backgroundColor: '#716ac5',
        color: '#fff',
      },
      ...(disabled
        ? {
            opacity: 0.5,
            pointerEvents: 'none',
          }
        : {}),
    };
  },
};

const btnTypeDefault = (style?: CSSProperties, disabled?: boolean): CSSProperties => {
  return { ...style, ...(disabled ? { pointerEvents: 'none' } : {}) };
};

const Button: FC<IProps> = ({
  type,
  btnType,
  outlined,
  size,
  className = '',
  isTableAction,
  style,
  children,
  title,
  loading,
  onClick,
  disabled,
  preventDebounce,
  ref,
  ariaLabel,
  testId,
}: IProps) => {
  /**
   * To handle multiple clicks on single button
   * in all places - we use debounced onClick func
   *
   * using `useCallback` prevents reinitializing of
   * the debounced func on rerender which causes
   * unexpected multiple calls of the onClick method
   */
  const debouncedOnClick = useCallback(onClick ? debounce(onClick, 100, true) : () => {}, []);
  const getType = (): string => btnType || 'primary';

  const currentStyle: CSSProperties = useMemo(
    () =>
      btnTypeMap[btnType as Exclude<IProps['btnType'], undefined>]
        ? btnTypeMap[btnType as Exclude<IProps['btnType'], undefined>]!(style, disabled)
        : btnTypeDefault(style, disabled),
    [btnType, disabled, style]
  );

  if (isTableAction) {
    return (
      <RbButton
        ref={ref}
        type={type}
        variant={`outline-${getType()}`}
        size={size}
        className={`p-0 rounded-pill ${className}`}
        style={{
          ...style,
          width: '1.5rem',
          height: '1.5rem',
          ...(disabled ? { pointerEvents: 'none' } : {}),
        }}
        title={title}
        onClick={preventDebounce ? onClick : debouncedOnClick}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {children}
      </RbButton>
    );
  }

  if (outlined)
    return (
      <RbButton
        ref={ref}
        type={type}
        variant={`outline-${getType()}`}
        size={size}
        className={className}
        style={
          btnType === 'light'
            ? {
                ...style,
                borderColor: '#ced4da',
                color: 'inherit',
                ...(disabled ? { pointerEvents: 'none' } : {}),
              }
            : {
                ...style,
                ...(disabled ? { pointerEvents: 'none' } : {}),
              }
        }
        title={title}
        onClick={preventDebounce ? onClick : debouncedOnClick}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {loading && <Spinner style={{ marginRight: 10 }} />}
        {children}
      </RbButton>
    );
  return (
    <RbButton
      ref={ref}
      type={type}
      variant={getType()}
      size={size}
      className={className}
      style={currentStyle}
      title={title}
      onClick={preventDebounce ? onClick : debouncedOnClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {loading && <Spinner style={{ marginRight: 10 }} />}
      {children}
    </RbButton>
  );
};

export default Button;
